import api from './api';
import { handleApiError } from './apiError';
import { MinimalBooking } from './bookingsApi';

/**
 * ── eventsApi — the event endpoints ─────────────────────────────────────
 *
 * Every type here is written camelCase because [[api]] converts the whole wire
 * both directions — see its docstring. So a `Body` type below is "the shape this
 * endpoint returns, after casing, before we drop anything", and it is a CLAIM
 * about the wire that nothing validates.
 * ───────────────────────────────────────────────────────────────────────
 */

// ── GET /home ────────────────────────────────────────────────────────────

/**
 * One event as `/home` returns it, narrowed to what the home carousels render.
 *
 * The response also carries `externalId`, `eventKind`, `priceFrom`,
 * `externalDetails`, the venue's `googleMapsUrl` and each image's `altText` /
 * `isGif` / `externalUrl`. None of them change what a card draws today, so they
 * stay off the type (the same rule authApi follows). Adding one back is a single
 * line here — the transform already delivers it camelCased.
 */
/**
 * The section keys `/home` ships today. These are VALUES, not keys, so the
 * transform leaves them kebab-case — don't camelCase them here.
 *
 * There's no `all-experiences` section and there won't be one — the screen's
 * fourth block is a grid over the whole catalogue, which is `listEvents` below.
 */
type HomeSectionType =
  | 'editorial-picks'
  | 'access-pass-exclusive'
  | 'secret-menus';

type HomeBody = {
  sections: { type: HomeSectionType; items: ShortEvent[] }[];
};

/**
 * `/home` as the screen wants it: one named list per carousel.
 *
 * The wire sends an ARRAY of `{type, items}`, which is the right shape for a
 * server that may add sections but the wrong one for this screen — it renders a
 * fixed set of carousels, each with its own design and its own order, so it would
 * only ever `find()` through that array. Pivoting once here means the screen
 * reads `payload.secretMenus`, and a section the server omits arrives as `[]`
 * rather than `undefined`.
 */
export type HomePagePayload = {
  editorialPicks: ShortEvent[];
  accessPassExclusive: ShortEvent[];
  secretMenus: ShortEvent[];
};

async function getHomePageEvents(): Promise<HomePagePayload> {
  const response = await api.get<HomeBody>('/home');

  if (!response.ok || !response.data) handleApiError(response);

  const { sections } = response.data;
  const itemsOf = (type: HomeSectionType): ShortEvent[] =>
    sections
      .find((section) => section.type === type)
      ?.items.filter((i) => !!i.venue) ?? [];

  return {
    editorialPicks: itemsOf('editorial-picks'),
    accessPassExclusive: itemsOf('access-pass-exclusive'),
    secretMenus: itemsOf('secret-menus'),
  };
}

// ── GET /accesspass-events-showcase ──────────────────────────────────────

/**
 * The showcase's section keys. These are VALUES, so [[api]] leaves them exactly
 * as sent — and the server mixes kebab with snake here (`unique-events` next to
 * `invite_only_experiences`). Don't tidy the spelling; it's what the wire sends.
 */
type ShowcaseSectionType =
  | 'unique-events'
  | 'invite_only_experiences'
  | 'first_tables'
  | 'exclusive_goodies';

/**
 * The same `{sections: [{type, items}]}` envelope `/home` uses, but the items are
 * PAST events — `next_bookable_slot` and `last_bookable_slot` both come back
 * null — so they are deliberately NOT `ShortEvent`. Nothing in the showcase is
 * bookable, and a showcase card draws a poster and a title, so that is all this
 * claims off the wire.
 */
type ShowcaseBody = {
  sections: {
    type: ShowcaseSectionType;
    items: ShortEvent[];
  }[];
};

/**
 * The showcase pivoted into one named list per carousel, for the same reason
 * [[HomePagePayload]] is: the landing page draws four fixed carousels in its own
 * order, each with its own copy, so it would only ever `find()` through the
 * array — and a section the server drops arrives as `[]`, not `undefined`.
 */
export type ShowcasePayload = {
  uniqueEvents: ShortEvent[];
  inviteOnlyExperiences: ShortEvent[];
  firstTables: ShortEvent[];
  exclusiveGoodies: ShortEvent[];
};

/**
 * The past events the Access Pass landing page shows off. Public — it is the
 * pitch made to someone who hasn't signed up.
 */
async function getAccessPassShowcase(): Promise<ShowcasePayload> {
  const response = await api.get<ShowcaseBody>('/accesspass-events-showcase');

  if (!response.ok || !response.data) handleApiError(response);

  const { sections } = response.data;
  const itemsOf = (type: ShowcaseSectionType): ShortEvent[] =>
    sections.find((section) => section.type === type)?.items ?? [];

  return {
    uniqueEvents: itemsOf('unique-events'),
    inviteOnlyExperiences: itemsOf('invite_only_experiences'),
    firstTables: itemsOf('first_tables'),
    exclusiveGoodies: itemsOf('exclusive_goodies'),
  };
}

// ── GET /events ──────────────────────────────────────────────────────────

/**
 * The query `/events` accepts — and this is ALL of it. No text search, and no
 * filtering by kind, category, venue, city, price or partner: the viewset
 * registers no filter backend, so an unrecognised param is ignored SILENTLY.
 * A 200 is therefore no evidence the server understood what you asked for.
 *
 * `date` / `from` / `to` are `YYYY-MM-DD` and match a slot's start only, in the
 * server's UTC day — see [[useAllExperiences]], which owns that translation.
 * `from`/`to` also don't match a slot that merely STRADDLES the range: an event
 * with slots in January and December doesn't answer a mid-year range.
 */
export type EventsQuery = {
  date?: string;
  from?: string;
  to?: string;
  /** `nextCursor` from the previous page; absent for the first. */
  cursor?: string | null;
  perPage?: number;
};

/** One page of `/events`, plus the cursor that fetches the one after it. */
export type EventsPage = {
  events: ShortEvent[];
  /** Null on the last page — nothing more to ask for. */
  nextCursor: string | null;
};

/**
 * The cursor envelope, same shape `/me/reservations` uses. Optional so a `page`
 * block the server stops sending can't crash the grid — the rows still render,
 * and the list simply reads as complete.
 */
type EventsListBody = {
  data: ShortEvent[];
  page?: {
    cursor: string | null;
    nextCursor: string | null;
    hasMore: boolean;
  };
};

/**
 * One page of every event eligible to show in the app. Public — no token, so
 * this is the same list signed out as signed in.
 *
 * Two filters are ALWAYS on and neither is overridable: live events only, and
 * nothing finished — an event drops out once every one of its slots has passed,
 * per EVENT and not per slot, so an event with a slot last week and one next
 * month stays in the list with both slots still on it. (`getEventById` has
 * neither filter; a past event's own page resolves, flagged `isInPast`.)
 *
 * ── The card's slot ignores your filter ──
 * `nextBookableSlot` is reduced over ALL of an event's bookable slots regardless
 * of the query, so a "tomorrow" page can contain a card that prints today's date,
 * and nothing in the response says which slot actually matched.
 */
async function listEvents(query: EventsQuery = {}): Promise<EventsPage> {
  const response = await api.get<EventsListBody>('/events', query);

  if (!response.ok || !response.data) handleApiError(response);

  const { data, page } = response.data;

  return {
    // The card asserts a venue AND a primary image (see EventCard), and this is
    // a far wider pool than `/home`'s curated sections — so an event it can't
    // draw is dropped here rather than left to crash the grid.
    events: data.filter(
      (event) => !!event.venue && event.images && event.images.length > 0
    ),
    nextCursor: page?.nextCursor ?? null,
  };
}

// ── GET /events/{id} ─────────────────────────────────────────────────────

/**
 * `/events/{id}` as the wire sends it: `Event` minus the one field it doesn't
 * carry. Derived from `Event` rather than spelled out again so the two can't
 * drift.
 */

/**
 * One event, read as-is.
 *
 * There is nothing to reshape any more, which is worth saying because this
 * function used to be the opposite. It SYNTHESISED two of every tier's fields —
 * `bookability` rebuilt from an older `isSoldOut` flag, and
 * `membershipsAppliedToTickettier` forced to null — because the wire sent neither.
 * Both arrive now, so both are simply read, and that alone is what turns the
 * blocked states on: a membership-gated tier draws its badges and its upsell, a
 * logged-out reader gets the login notice instead of a stepper, a tier on a
 * finished slot says so, and an exhausted per-user allowance links to the
 * bookings that used it. Under the synthesis every one of those read as plainly
 * bookable. Nothing above this layer moved when they landed — the screens were
 * already written against the real shape — which was the point of doing the
 * synthesis here rather than in a component.
 *
 * ── Don't reintroduce a sold-out flag ──
 * The wire still sends a per-tier `isSoldOut`, and it's deliberately NOT on
 * `EventSlotTicketTier`: `bookability` subsumes it (sold out arrives as
 * `reason.type === 'sold_out'`, which is what `TicketTier` draws its badge from).
 * Typing both would give a tier two answers to one question, and the server —
 * not us — is what ranks sold-out against the reasons that outrank it. It rides
 * along at runtime untyped, exactly like `tax` and the other fields below.
 *
 * `externalUrl` is the one thing invented here: the detail payload doesn't carry
 * it, so no event read through this function is ever external.
 */
async function getEventById(id: string): Promise<Event> {
  const response = await api.get<Event>(`/events/${id}`);

  if (!response.ok || !response.data) handleApiError(response);

  // Two fields read straight off the wire's own shape and folded onto typed
  // ones — see `WireTierExtras`. `approvalQueries` (a generic {key: required}
  // map; this app only ever asks the one question) collapses onto
  // `requiresSocialLink`, and the wire's own `participationRequest` — a
  // REDUCED row, just `bookingId`, no nested `booking` — onto
  // `ShortParticipationRequest` (see that type; the standalone
  // `participationRequestsApi` fetch is the full row).
  for (const slot of response.data.slots) {
    for (const tier of slot.ticketTiers) {
      const wire = tier as unknown as WireTierExtras;
      tier.requiresSocialLink = !!wire.approvalQueries?.socialLink;
      tier.participationRequest = wire.participationRequest;

      if (tier.requiresGuestApproval) {
        response.data.isRsvpEvent = true;
      }
    }
  }

  return response.data;
}

/**
 * The two tier fields `getEventById` reads straight off the wire before
 * folding them onto `EventSlotTicketTier`'s typed ones. Kept separate from
 * that interface — nothing downstream reads either shape in ITS raw form,
 * only the folded `requiresSocialLink` / `participationRequest`.
 */
type WireTierExtras = {
  approvalQueries: { socialLink?: boolean };
  participationRequest: {
    id: string;
    status: ShortParticipationRequestStatus;
    approvalQueries: { socialLink?: string };
    bookingId: string | null;
    createdAt: string;
  } | null;
};

// ── The event shapes, shared by both endpoints above ─────────────────────
//
// `ShortEvent` is the item `/home` returns; `Event` is the same thing plus the
// fields only the detail response carries. Both were checked against a real
// staging response on 2026-08-04, so they're no longer the bruno-derived sketch
// they started as.
//
// The wire also sends `externalId`, `eventKind`, `shortDescription`, `isPaid`,
// `externalDetails`, `labelOverrides`, `tag`, per-tier `tax` and per-tier
// `isSoldOut`. They stay off the types under this file's rule — a field lands
// here when a screen renders it — and each is a one-line addition when one does.
// `isSoldOut` is the one that's off them for a stronger reason than "unused":
// `bookability` already answers it, and typing both would let a tier disagree
// with itself (see `getEventById`).

interface EventImage {
  id: string;
  url: string;
  isPrimary: boolean;
}

interface EventVenue {
  name: string;
  googleMapsUrl: string;
}

export interface PSPrice {
  amountInMinorUnits: number;
  currency: string;
  display: string;
}

// ── Bookability: whether a tier can be booked, and why not ───────────────
//
// We block a booking at two levels, and they're different surfaces. The EVENT
// level is drawn once at the top of the page and has exactly two reasons, both
// already on `Event` (`isInPast`, `isSoldOut`). Everything else is TICKET level,
// and lives here: the reason AND whatever the reader needs to act on it, so the
// card is handed a state to draw instead of a pile of flags to reason about.
//
// The server resolves PRECEDENCE. Several reasons can hold at once (a sold-out
// tier the user also lacks the membership for) and exactly one arrives; the
// order — slot_in_past, sold_out, unauthenticated, max_seats_per_user_exhausted,
// membership_required, limit_reached — is not the client's to re-derive.
//
// The `type` values stay snake_case on purpose: `api` converts KEYS, never
// values (same as `HomeSectionType` above), so what the wire sends is what we
// compare against.

/**
 * A membership plan offered as the way OUT of a blocked tier.
 *
 * The wire also sends `basePrice` and `taxAmount`; a card prints one number, so
 * only `priceWithTax` lands here (this file's rule — a field arrives when a
 * screen renders it). `id` is here to route to checkout, not to display.
 */
export interface MembershipPlan {
  id: string;
  name: string;
  description: string;
  priceWithTax: PSPrice;
}

/**
 * A membership a tier REQUIRES — one badge on the card.
 *
 * Distinct from `MembershipPlan`, which is a plan to BUY and carries the price
 * and pitch to sell it with. This one is only ever a LABEL, so `name` is all
 * that renders; `id` stays because it names the same membership a
 * `membership_required` reason offers as its `membershipsRequiredToBook` — the one
 * correlation between the two shapes.
 *
 * `kind` tells the members-only band whether any of the event's tiers take the
 * Trial Pass (see [[derivePlanToUpsellInMembersOnlyTicketTierGroup]]).
 */
export interface MembershipTier {
  id: string;
  kind: 'trial' | 'access_pass';
  name: string;
}

/** A per-user seat allowance on ONE tier. Only sent once it's all gone. */
export interface SeatUsage {
  limit: number;
  used: number;
}

/**
 * A membership's allowance for one category of event, and when it comes back.
 *
 * `periodResetsAt` is null when nothing will reset it — the user cancelled, or
 * their plan doesn't renew (a trial pass). That's a real state with no date to
 * promise, not a missing value.
 *
 * The wire also sends `key` ('ps_original') and `periodStartedAt`; we render the
 * `label` and the reset date, so neither lands here.
 */
export interface CategoryUsage {
  label: string;
  periodResetsAt: string | null;
  limit: number;
  used: number;
}

/** Why a tier can't be booked. Exactly one arrives — the server ranks them. */
export type TierBlockedReason =
  /**
   * The tier's slot has already happened. Ranked FIRST so it can't be masked by
   * a lesser reason — screens drop past slots outright rather than draw them.
   */
  | { type: 'slot_in_past' }
  | { type: 'sold_out' }
  | { type: 'unauthenticated' }
  | {
      type: 'max_seats_per_user_exhausted';
      usage: SeatUsage;
      /** The bookings that used the allowance up — enough to link to them. */
      bookings: { id: string }[];
    }
  | { type: 'membership_required'; membershipsRequiredToBook: MembershipPlan[] }
  | {
      type: 'limit_reached';
      usage: CategoryUsage;
      /** null when they're already on the highest plan — nothing left to buy. */
      membershipsRequiredToBook: MembershipPlan[] | null;
    }
  | {
      type: 'request_pending_approval';
      request: {
        id: string;
      };
    }
  | {
      /** An organizer already approved this user's application — a booking
       * exists, so there's nothing left to apply for. Same shape as
       * `request_pending_approval`; `request.id` is the participation
       * request's own id, NOT the booking's — get that off the tier's own
       * `participationRequest.bookingId` instead (see that type). */
      type: 'booking_confirmed_cannot_reapply';
      request: {
        id: string;
      };
    };

/** Statuses a participation request can settle into once an organizer reviews it. */
export type ShortParticipationRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'blocked';

/**
 * The participation-request row as it arrives NESTED under a tier on
 * `GET /events/{id}` — a REDUCED version of the row a standalone fetch
 * returns (see `participationRequestsApi`'s `ParticipationRequest`, which
 * nests the full `Booking` once one exists; this only has its id).
 *
 * `socialLink` is the same collapse `requiresSocialLink` does on the tier
 * itself: the wire's `approvalQueries` is a generic `{key: answer}` map, and
 * this app only ever asks the one question.
 */
export type ShortParticipationRequest = {
  id: string;
  status: ShortParticipationRequestStatus;
  approvalQueries: {
    socialLink?: string;
  };
  /** Set once an organizer approves. Read this, not `reason.request.id`, to
   * route to the booking — see `TierBlockedReason`'s
   * `booking_confirmed_cannot_reapply`. */
  bookingId: string | null;
  createdAt: string;
};

/**
 * The server's verdict on one tier.
 *
 * Discriminated on `isBookable` so the two can't disagree: a bookable tier
 * cannot carry a reason, and a reason cannot be read without first checking.
 */
export type TierBookability =
  | { isBookable: true; reason: null }
  | { isBookable: false; reason: TierBlockedReason };

export interface EventSlotTicketTier {
  slotTierId: string;
  ticketTierId: string;
  name: string;
  description: string;
  price: PSPrice;
  taxAmount: PSPrice;
  priceWithTax: PSPrice;
  /**
   * The stepper's ceiling. Does NOT account for the user's prior bookings.
   *
   * The wire spells it `max_seats_per_user`, so this is `maxSeatsPerUser` — it
   * was `maxBookingsPerUser` here, which no payload has ever carried, so it read
   * `undefined` and every clamp built on it (`Math.min(undefined, …)` in
   * [[eventCart]]) came out NaN. Nothing validates these types against the wire
   * (see this file's header), so the only thing keeping it honest is the name.
   */
  maxSeatsPerUser: number;
  bookability: TierBookability;
  /**
   * The memberships that may book this tier — one badge each — or NULL when
   * anyone can. Null is the ungated case, not an absent field: an empty array
   * would read as "gated to nobody", which is a different (and impossible)
   * claim, so don't collapse the two.
   *
   * Independent of whether THIS reader can book (that's `bookability`'s answer):
   * a member sees the badges AND a stepper.
   */
  membershipsAppliedToTickettier: MembershipTier[] | null;
  seatingCapacity: number;
  availableCapacity: number;

  // approval only events fields
  requiresGuestApproval: boolean;
  /** Derived in `getEventById` from the wire's `approvalQueries.socialLink` — not a literal field; see `WireTierExtras`. */
  requiresSocialLink: boolean;
  /** The viewer's own latest application to THIS tier, or null if they never
   * applied — only ever set on a `requiresGuestApproval` tier. Folded in by
   * `getEventById`, same as `requiresSocialLink`; see `ShortParticipationRequest`. */
  participationRequest: ShortParticipationRequest | null;
}

export interface EventSlot {
  id: string;
  startAt: string;
  endAt: string;
  maxAttendees: number;
  ticketTiers: EventSlotTicketTier[];
  isInPast: boolean;
}

export interface OrgDetails {
  name: string;
  id: string;
}

export interface ShortEvent {
  id: string;
  title: string;
  images: EventImage[];
  venue: EventVenue;
  nextBookableSlot: {
    startDatetime: string;
    endDatetime: string;
  };
  kind: 'ticketed' | 'external' | 'secret_menu';
  category: 'ps_original' | 'preview' | 'normal' | null;
  lastBookableSlot: {
    startDatetime: string;
    endDatetime: string;
  };
  isSoldOut: boolean;
  isInPast: boolean;
  priceFrom: PSPrice;
  // we don't know what it's type is yet, but do know that this field exists in the wire response
  externalUrl: string | null;
  orgDetails: OrgDetails | null;
}

export interface Event extends ShortEvent {
  termsAndConditions: string;
  slots: EventSlot[];
  description: string;
  isRsvpEvent: boolean;
  bookings: Omit<MinimalBooking, 'event'>[];

  /** The server's own: an event is past once every one of its slots is. */
}

export default {
  getHomePageEvents,
  getAccessPassShowcase,
  listEvents,
  getEventById,
};
